// Keep the API and renderer copies in sync. Electron imports the renderer copy.
// Unknown preset formats are allowed; known unrelated or active file types are not.
export const MAX_BLUEPRINT_FILE_SIZE = 10_000_000;
export const BLUEPRINT_SIZE_ERROR =
  'Blueprint files must be non-empty and no larger than 10 MB.';
export const BLUEPRINT_AUDIO_ERROR =
  'Audio files are samples, not blueprints. Attach audio in the preview field instead.';
export const BLUEPRINT_MEDIA_ERROR =
  'Images and videos cannot be uploaded as blueprints. Use the artwork field for a cover image.';
export const BLUEPRINT_UNSAFE_ERROR =
  'Programs, installers, scripts, and web pages cannot be shared as blueprints.';
export const BLUEPRINT_ARCHIVE_ERROR =
  'Upload the preset or rack file directly. Archives and disk images are not supported.';
export const BLUEPRINT_DOCUMENT_ERROR =
  'Documents cannot be uploaded as blueprints. Upload the preset or rack file instead.';

const audioExtension =
  /\.(wav|wave|bwf|rf64|w64|aif|aiff|aifc|flac|mp1|mp2|mp3|mpa|ogg|oga|opus|spx|m4a|m4b|m4p|aac|adts|ac3|eac3|wma|caf|au|snd|ape|wv|tta|dsf|dff|amr|awb)$/i;
const mediaExtension =
  /\.(png|jpe?g|jpe|jfif|webp|gif|bmp|dib|tiff?|svgz?|ico|icns|cur|heic|heif|avif|jxl|jp2|psd|psb|exr|hdr|tga|dng|cr2|cr3|nef|arw|orf|raf|rw2|pef|srw|mp4|m4v|mov|qt|avi|mkv|webm|wmv|flv|f4v|mpg|mpeg|mpe|m2v|3gp|3g2|ogv|vob|mts|m2ts|mxf|asf|rm|rmvb)$/i;
// Match dangerous suffixes in double extensions too (for example, setup.exe.fxp).
const unsafeExtension =
  /\.(exe|dll|com|scr|cpl|msi|msp|msix|appx|appxbundle|msixbundle|app|pkg|deb|rpm|apk|aab|jar|class|wasm|so|dylib|vst|vst3|component|bundle|sh|bash|zsh|fish|command|bat|cmd|ps1|psm1|psd1|vbs|vbe|wsf|wsh|js|jse|mjs|cjs|jsx|ts|tsx|py|pyc|pyo|rb|pl|pm|php\d*|phtml|pht|phar|asp|aspx|jsp|jspx|cgi|lua|scpt|scptd|applescript|workflow|action|automator|hta|html?|xhtml|shtml|lnk|url|desktop|webloc|reg|inf|iso|img|dmg)(?:\.|$)/i;
const archiveExtension =
  /\.(zip|zipx|rar|7z|tar|gz|tgz|bz2|tbz2?|xz|txz|zst|tzst|lz|lzma|cab|wim|vhdx?|vmdk|qcow2|sparseimage|sparsebundle)$/i;
const documentExtension =
  /\.(pdf|rtf|docx?|docm|dotx?|dotm|xlsx?|xlsm|xlsb|xltx?|xltm|pptx?|pptm|ppsx?|ppsm|potx?|potm|odt|ods|odp|pages|numbers|keynote|epub|mobi|chm|swf)$/i;
const unsafeMimes = new Set([
  'application/x-msdownload',
  'application/x-dosexec',
  'application/vnd.microsoft.portable-executable',
  'application/x-executable',
  'application/x-pie-executable',
  'application/x-sharedlib',
  'application/java-archive',
  'application/java-vm',
  'application/wasm',
  'application/vnd.android.package-archive',
  'application/x-ms-shortcut',
  'application/javascript',
  'application/ecmascript',
  'text/javascript',
  'text/ecmascript',
  'text/html',
  'application/xhtml+xml',
  'application/x-httpd-php',
  'application/x-sh',
  'application/x-shellscript',
  'text/x-shellscript',
  'text/x-python',
  'application/x-python-code',
  'text/x-perl',
  'text/x-ruby',
  'application/x-powershell',
]);
const archiveMimes = new Set([
  'application/zip',
  'application/x-zip-compressed',
  'application/x-7z-compressed',
  'application/vnd.rar',
  'application/x-rar-compressed',
  'application/x-tar',
  'application/x-bzip2',
  'application/x-xz',
  'application/zstd',
  'application/x-iso9660-image',
  'application/x-apple-diskimage',
  'application/vnd.ms-cab-compressed',
]);
const documentMimes =
  /^(application\/(?:pdf|rtf|msword|vnd\.ms-|vnd\.openxmlformats-officedocument\.|vnd\.oasis\.opendocument\.|epub\+zip|x-shockwave-flash|x-ole-storage)|text\/rtf)/i;
const mimeName = (value: string) => value.split(';')[0].trim().toLowerCase();

export function isBlueprintAudioFile(
  fileName: string,
  contentType = ''
): boolean {
  const mime = mimeName(contentType);
  return (
    audioExtension.test(fileName.normalize('NFKC').trim()) ||
    (mime.startsWith('audio/') &&
      !['audio/midi', 'audio/x-midi'].includes(mime)) ||
    ['application/ogg', 'application/x-flac'].includes(mime)
  );
}

/** Shared metadata screening; MIME is only one signal, never proof of safety. */
export function getBlueprintFileError(
  fileName: string,
  contentType = '',
  size?: number
): string | null {
  if (
    size !== undefined &&
    (!Number.isSafeInteger(size) || size <= 0 || size > MAX_BLUEPRINT_FILE_SIZE)
  )
    return BLUEPRINT_SIZE_ERROR;
  const name = fileName.normalize('NFKC');
  if (
    !name.trim() ||
    /[\\/:*?"<>|]/.test(name) ||
    /[. ]$/.test(name) ||
    [...name].some((c) => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127) ||
    /[\u202a-\u202e\u2066-\u2069]/.test(name) ||
    /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i.test(name)
  )
    return 'Invalid blueprint filename.';
  const mime = mimeName(contentType);
  if (isBlueprintAudioFile(name, mime)) return BLUEPRINT_AUDIO_ERROR;
  if (mediaExtension.test(name) || /^(image|video)\//.test(mime))
    return BLUEPRINT_MEDIA_ERROR;
  if (unsafeExtension.test(name) || unsafeMimes.has(mime))
    return BLUEPRINT_UNSAFE_ERROR;
  if (
    archiveExtension.test(name) ||
    (archiveMimes.has(mime) &&
      !(
        /\.multipass$/i.test(name) &&
        ['application/zip', 'application/x-zip-compressed'].includes(mime)
      ))
  )
    return BLUEPRINT_ARCHIVE_ERROR;
  if (documentExtension.test(name) || documentMimes.test(mime))
    return BLUEPRINT_DOCUMENT_ERROR;
  return null;
}

/** Bounded signature screening. It is not an antivirus scan or a preset parser. */
export function getBlueprintHeaderError(bytes: Uint8Array): string | null {
  const at = (text: string, offset = 0) => {
    if (offset + text.length > bytes.length) return false;
    for (let i = 0; i < text.length; i++)
      if (bytes[offset + i] !== text.charCodeAt(i)) return false;
    return true;
  };
  const starts = (hex: number[]) =>
    hex.length <= bytes.length && hex.every((b, i) => bytes[i] === b);
  const riff = ['RIFF', 'RIFX', 'RF64', 'BW64'].some((s) => at(s));
  if (
    (riff && at('WAVE', 8)) ||
    (at('FORM') && (at('AIFF', 8) || at('AIFC', 8))) ||
    (at('ftyp', 4) && ['M4A ', 'M4B ', 'M4P '].some((s) => at(s, 8))) ||
    [
      'fLaC',
      'caff',
      '.snd',
      'wvpk',
      'MAC ',
      'DSD ',
      'TTA1',
      'ID3',
      '#!AMR',
    ].some((s) => at(s))
  )
    return BLUEPRINT_AUDIO_ERROR;
  if (at('OggS')) {
    const packet = 27 + (bytes[26] || 0);
    return ['OpusHead', 'Speex   ', '\x01vorbis', '\x7fFLAC'].some((s) =>
      at(s, packet)
    )
      ? BLUEPRINT_AUDIO_ERROR
      : BLUEPRINT_MEDIA_ERROR;
  }
  if (bytes.length >= 4 && bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0) {
    const mpeg =
      (bytes[1] & 0x18) !== 0x08 &&
      (bytes[1] & 0x06) !== 0 &&
      (bytes[2] & 0xf0) !== 0 &&
      (bytes[2] & 0xf0) !== 0xf0 &&
      (bytes[2] & 0x0c) !== 0x0c;
    const adts =
      bytes.length >= 7 &&
      (bytes[1] & 0xf6) === 0xf0 &&
      ((bytes[2] >> 2) & 0x0f) < 13;
    if (mpeg || adts) return BLUEPRINT_AUDIO_ERROR;
  }
  if (
    starts([137, 80, 78, 71, 13, 10, 26, 10]) ||
    starts([255, 216, 255]) ||
    ['GIF87a', 'GIF89a', 'BM', 'II\x2a\x00', 'MM\x00\x2a', '8BPS', 'icns'].some(
      (s) => at(s)
    ) ||
    starts([0, 0, 1, 0]) ||
    starts([0, 0, 2, 0]) ||
    starts([0xff, 0x0a]) ||
    (riff && (at('WEBP', 8) || at('AVI ', 8))) ||
    at('ftyp', 4) ||
    at('moov', 4) ||
    at('mdat', 4) ||
    at('FLV') ||
    starts([0x1a, 0x45, 0xdf, 0xa3]) ||
    starts([0, 0, 1, 0xba]) ||
    starts([0, 0, 1, 0xb3]) ||
    starts([0x30, 0x26, 0xb2, 0x75, 0x8e, 0x66, 0xcf, 0x11, 0xa6, 0xd9])
  )
    return BLUEPRINT_MEDIA_ERROR;
  if (
    at('MZ') ||
    at('\x7fELF') ||
    at('\x00asm') ||
    [
      [0xfe, 0xed, 0xfa, 0xce],
      [0xce, 0xfa, 0xed, 0xfe],
      [0xfe, 0xed, 0xfa, 0xcf],
      [0xcf, 0xfa, 0xed, 0xfe],
      [0xca, 0xfe, 0xba, 0xbe],
      [0xbe, 0xba, 0xfe, 0xca],
      [0xca, 0xfe, 0xba, 0xbf],
      [0xbf, 0xba, 0xfe, 0xca],
    ].some(starts) ||
    starts([
      0x4c, 0, 0, 0, 1, 0x14, 2, 0, 0, 0, 0, 0, 0xc0, 0, 0, 0, 0, 0, 0, 0x46,
    ])
  )
    return BLUEPRINT_UNSAFE_ERROR;
  if (
    at('PK\x03\x04') ||
    at('PK\x05\x06') ||
    at('PK\x07\x08') ||
    at('Rar!\x1a\x07') ||
    starts([0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c]) ||
    at('BZh') ||
    starts([0xfd, 0x37, 0x7a, 0x58, 0x5a, 0]) ||
    starts([0x28, 0xb5, 0x2f, 0xfd]) ||
    at('MSCF') ||
    at('ustar', 257) ||
    at('CD001', 0x8001) ||
    at('koly', Math.max(0, bytes.length - 512))
  )
    return BLUEPRINT_ARCHIVE_ERROR;
  if (
    at('%PDF-') ||
    starts([0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]) ||
    ['FWS', 'CWS', 'ZWS'].some((s) => at(s))
  )
    return BLUEPRINT_DOCUMENT_ERROR;
  const prefix = bytes.subarray(0, 65536);
  const encoding =
    starts([0xff, 0xfe]) || starts([0x3c, 0])
      ? 'utf-16le'
      : starts([0xfe, 0xff]) || starts([0, 0x3c])
        ? 'utf-16be'
        : 'utf-8';
  const text = new TextDecoder(encoding)
    .decode(prefix)
    .replace(/^\uFEFF/, '')
    .trimStart();
  // Skip XML prolog/comments without parsing or resolving entities.
  const root = text.replace(
    /^(?:(?:<\?xml[\s\S]*?\?>|<!--[\s\S]*?-->)\s*)+/i,
    ''
  );
  if (
    /^(?:#!|<\?(?:php|=)|<%|@echo\s+off\b|\[InternetShortcut\]|\[Desktop Entry\]|<!doctype\s+html\b|<(?:html|head|body|script|iframe)\b)/i.test(
      root
    )
  )
    return BLUEPRINT_UNSAFE_ERROR;
  if (/^<(?:[\w.-]+:)?svg[\s/>]/i.test(root)) return BLUEPRINT_MEDIA_ERROR;
  if (/^\{\\rtf/i.test(root)) return BLUEPRINT_DOCUMENT_ERROR;
  if (text.startsWith('<') && /<!DOCTYPE|<!ENTITY/i.test(text))
    return BLUEPRINT_UNSAFE_ERROR;
  return null;
}

/** Preserve unknown extensions and casing; extensionless files stay extensionless. */
export function blueprintSourceExtension(fileName: string): string {
  const error = getBlueprintFileError(fileName);
  if (error) throw new Error(error);
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(dot) : '';
}
